import React, { memo } from "react";
import StarIcon from "@mui/icons-material/Star";
import FavoriteIcon from "@mui/icons-material/Favorite";
import { Link } from "react-router-dom";
import { getDiscount } from "../../utils/functions";
import { useDispatch, useSelector } from "react-redux";
import {
  addToWishlist,
  removeFromWishlist,
} from "../../actions/wishlistAction";
import { useSnackbar } from "notistack";

// Component for Price Display
const Price = ({ price, cuttedPrice }) => (
  <div className="flex items-baseline gap-2 sm:gap-4 text-lg font-semibold text-gray-800 mt-2 sm:mt-3">
    <span className="text-xl sm:text-2xl font-bold text-gray-900">
      ₹{price.toLocaleString()}
    </span>
    {cuttedPrice > price && (
      <span className="text-sm text-gray-500 line-through">
        ₹{cuttedPrice.toLocaleString()}
      </span>
    )}
    {cuttedPrice > price && (
      <span className="text-sm text-primary-green font-semibold">
        {getDiscount(price, cuttedPrice)}% OFF
      </span>
    )}
  </div>
);

// Component for Rating Display
const Rating = ({ ratings, numOfReviews }) => (
  <span className="text-sm text-gray-600 font-medium flex gap-1 items-center">
    <span className="text-xs px-2 py-1 bg-primary-green rounded-full text-white flex items-center gap-0.5">
      {ratings.toFixed(1)} <StarIcon sx={{ fontSize: "14px" }} />
    </span>
    <span className="text-xs text-gray-500">
      ({numOfReviews.toLocaleString()})
    </span>
  </span>
);

const Product = memo(
  ({ _id, name, images, ratings, numOfReviews, price, cuttedPrice }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const { wishlistItems } = useSelector((state) => state.wishlist);
    const itemInWishlist = wishlistItems.some((i) => i.product === _id);

    const addToWishlistHandler = () => {
      if (itemInWishlist) {
        dispatch(removeFromWishlist(_id));
        enqueueSnackbar("Removed from Wishlist", { variant: "success" });
      } else {
        dispatch(addToWishlist(_id));
        enqueueSnackbar("Added to Wishlist", { variant: "success" });
      }
    };

    return (
      <div className="group flex flex-col items-center px-4 py-4 bg-white shadow-md hover:shadow-lg rounded-lg relative transition-shadow duration-300 h-full max-w-xs sm:max-w-md">
        {/* Image & Product Title */}
        <Link
          to={`/product/${_id}`}
          className="flex flex-col items-center text-center group"
        >
          <div className="w-32 h-32 sm:w-40 sm:h-40 md:w-52 md:h-52 overflow-hidden rounded-lg transition-transform duration-300 transform group-hover:scale-105">
            <img
              draggable="false"
              className="w-full h-full object-contain"
              src={images[0].url}
              alt={name}
            />
          </div>
          <h2 className="mt-3 text-sm md:text-base font-semibold text-gray-800 group-hover:text-primary-blue transition-colors duration-300">
          {name.length > 50 ? `${name.substring(0, 50)}...` : name}
        </h2>
        </Link>

        <div className="flex items-center gap-2 mt-3 text-gray-600">
          <span className="text-xs px-2 py-1 bg-primary-green rounded-full text-white flex items-center">
            {ratings.toFixed(1)} <StarIcon sx={{ fontSize: "14px" }} />
          </span>
          <span className="text-xs">({numOfReviews.toLocaleString()})</span>
        </div>

        {/* Price and Discount */}
        <div className="flex flex-wrap items-center gap-2 mt-3 text-gray-800 text-base md:text-lg">
          <span className="text-xl font-bold text-gray-900">
            ₹{price.toLocaleString()}
          </span>
          {cuttedPrice > price && (
            <>
              <span className="text-sm line-through text-gray-500">
                ₹{cuttedPrice.toLocaleString()}
              </span>
              <span className="text-sm text-primary-green font-semibold">
                {getDiscount(price, cuttedPrice)}% OFF
              </span>
            </>
          )}
        </div>

        {/* Wishlist Button */}
        <button
          onClick={addToWishlistHandler}
          className={`absolute top-3 right-3 p-2 rounded-full bg-white shadow-md transition-all duration-300 transform scale-90 hover:scale-110 ${
            itemInWishlist ? "text-red-500" : "text-gray-400 hover:text-red-500"
          }`}
        >
          <FavoriteIcon sx={{ fontSize: "24px" }} />
        </button>
      </div>
    );
  }
);

export default Product;
